import React from "react";
import { observer } from "mobx-react";
import { useStores } from "netbank-shared/src/hooks";
import { IInfoPopoverProps } from "~shared/InfoPopover/InfoPopover";
import { tx } from "netbank-shared/src/libs/i18n";
import santanderIcon from "~assets/logo-icon-plain.svg";
import { AccountType } from "netbank-shared/src/libs/models/Content/Enums";
import { IDepositAccount } from "netbank-shared/src/libs/models/Engagements";
import { Dropdown } from "~views/shared/Dropdown/Dropdown";
import { ISelectOption } from "netbank-shared/src/libs/models/Select/SelectOption";
import { Button } from "~views/shared/Button/Button";
import plusIcon from "~assets/plus.svg";

export interface ListItem {
  index: number;
  value: string;
  label: string;
  suffix?: string;
  valueSuffix?: string;
  extra?: any;
  icon?: string;
  iconAlt?: string;
  disabled?: boolean;
  disabledText?: string;
}

interface ITransferAccountDropdownProps {
  value: string;
  label: string;
  setCreateToAccount: (value: boolean) => void;
  createToAccount: boolean;
  onChange: (item?: ISelectOption) => void;
  subtext?: string;
  icon?: string;
  suffix?: string;
  infoPopover?: IInfoPopoverProps;
  ownAccounts?: IDepositAccount[];
  error?: string;
  disabledText?: string;
  disabled?: boolean;
}

export const TransferAccountDropdown = observer(
  ({
    value,
    label,
    subtext,
    icon,
    suffix,
    infoPopover,
    onChange,
    createToAccount,
    setCreateToAccount,
    ownAccounts,
    error,
    disabledText,
  }: ITransferAccountDropdownProps): JSX.Element => {
    const { transferStore } = useStores();

    const ownAccountList = ownAccounts?.map((account, index): ListItem => {
      const isAccountDeposit = account.accountType === AccountType.Deposit;
      return {
        index,
        value: account.accountNumber,
        label: account.accountPersonalName || account.name || account.nameIB || "",
        valueSuffix: ` | ${account.displayNumber}`,
        icon: santanderIcon,
        disabled: isAccountDeposit && account.isActivationPeriodExpired && !account.isMatured,
        disabledText,
      };
    });

    const savedAccounts: ListItem[] =
      transferStore.savedAccountsViableForTranfer?.map((account, index) => {
        return {
          index,
          value: account.accountNumber,
          label: account.displayName || `${account.clearingNumber}-${account.accountNumber}`,
          valueSuffix: account.displayName && ` | ${account.clearingNumber}-${account.accountNumber}`,
        };
      }) || [];

    const onClick = (item: ISelectOption) => {
      onChange(item);
    };

    return (
      <>
        <Dropdown
          infoPopover={infoPopover}
          label={label}
          subtext={subtext}
          suffix={suffix}
          suffixLabel={tx("transfer.availableAmount")}
          icon={!createToAccount ? icon : undefined}
          value={value}
          innerLabel={tx("placeholders.chooseAccount")}
          onChange={onClick}
          list={[...(ownAccountList || []), ...(savedAccounts || [])]}
          error={error}
        />
        <div style={{ display: "flex", justifyContent: "end" }}>
          <Button
            type="text"
            iconPrefix={plusIcon}
            title={tx("transfer.addNewReceiver")}
            onClick={() => {
              onChange(undefined);
              setCreateToAccount(!createToAccount);
            }}
          />
        </div>
      </>
    );
  }
);
